import { ModalConfirmation, ModalConfirmationProps, notify, useModal } from '@billy/design-system'
import { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { queryClient } from '../../../../config/queryClient'
import { NotificationKeys } from '../../../../enums/notificationKeys'
import { QueryKeys } from '../../../../enums/queryKeys'
import { APIError } from '../../../../utils'
import { getErrorMessage } from '../../../../utils/getErrorMessage'
import { useVoidBillPayment } from '../../hooks/useVoidBillPayment'

interface VoidBillPaymentConfirmationModalProps extends ModalConfirmationProps {
  paymentId: string
}

export const VoidBillPaymentConfirmationModal = ({
  paymentId,
  id,
  ...props
}: VoidBillPaymentConfirmationModalProps): ReactElement => {
  const { t } = useTranslation()

  const { close: closeVoidConfirmationModal } = useModal(id)

  const { update: voidBillPayment, isLoading: isBillUpdating } = useVoidBillPayment({
    onSuccess: () => {
      notify({
        id: NotificationKeys.BillPaymentVoided,
        message: t('bill.void_payment_modal.void_success'),
        variant: 'success',
      })
      queryClient.invalidateQueries([QueryKeys.Bills])
      queryClient.invalidateQueries([QueryKeys.Transactions])
      closeVoidConfirmationModal()
    },
    onError: (error: APIError | undefined) => {
      const message = getErrorMessage(error) || 'Could not void the payment'
      notify({ id: NotificationKeys.BillPaymentVoided, message, variant: 'error' })
    },
  })

  const handleVoidModalOkClick = useCallback(() => {
    voidBillPayment({ paymentId, payload: { id: paymentId, isVoided: true } })
  }, [paymentId, voidBillPayment])

  return (
    <ModalConfirmation
      {...props}
      id={id}
      cancelLabel={t('cancel')}
      danger
      message={t('bill.void_payment_modal.message')}
      okLabel={t('bill.void_payment_modal.ok')}
      okLoading={isBillUpdating}
      onOk={handleVoidModalOkClick}
      title={t('bill.void_payment_modal.title')}
    />
  )
}
